import { useParams } from 'react-router-dom';
import { FontWeights } from '@newcore/ui/enums/font-weights';
import { TextSizes } from '@newcore/ui/enums/typography-text-sizes';
import { Text } from '@newcore/ui/typography';
import { ROUTES } from '@/common/auth-routes';
import { buildTenantPath } from '@/common/tenant-utils';
import { validateOnboardingToken } from '@/features/onboarding/api/onboarding-api';
import { InstallStep } from '@/features/onboarding/components/install-step';
import { MobileInstallApp } from '@/features/onboarding/components/mobile-install-app';
import { OnboardingBackground } from '@/features/onboarding/components/onboarding-background';
import { OnboardingTokenValidationStatusScreen } from '@/features/onboarding/components/onboarding-token-validation-status-screen';
import { OnboardingWizardContainer } from '@/features/onboarding/components/onboarding-wizard-container';
import { Onboarding } from '@/features/onboarding/components/primitives';
import { UserOnboardingWizard } from '@/features/onboarding/components/wizards';
import {
  useTokenValidation,
  ValidationStatus,
} from '@/features/onboarding/hooks/use-token-validation';
import {
  createOnboardingDeepLinkUrl,
  createOnboardingUniversalLinkUrl,
  isMobileDevice,
} from '@/features/onboarding/utils/mobile-platform';

const INVITE_INSTALL_TITLE = 'Activate your account in the NewCore app';
const INVITE_INSTALL_CTA_LABEL = 'Activate in the NewCore app';
const INVITE_INSTALL_PAGE_TITLE = 'Activate your account - NewCore';

export function OnboardingPage() {
  const { token } = useParams<{ token: string }>();

  const { status, validationResult, error } = useTokenValidation({
    token,
    validateToken: validateOnboardingToken,
  });

  const handleComplete = () => {
    const tenantSlug = validationResult?.tenant_slug;
    window.location.href = tenantSlug
      ? buildTenantPath(tenantSlug, ROUTES.DASHBOARD)
      : ROUTES.TENANT_SELECTION;
  };

  if (status !== ValidationStatus.VALID) {
    return (
      <OnboardingTokenValidationStatusScreen
        status={status}
        validationResult={validationResult}
        error={error}
        buildSignInUrl={tenantSlug =>
          buildTenantPath(tenantSlug, ROUTES.DASHBOARD)
        }
      />
    );
  }

  const isMobileOnboardingEligible =
    validationResult?.is_mobile_onboarding_eligible ?? false;

  if (isMobileOnboardingEligible && token && isMobileDevice()) {
    return (
      <>
        <title>{INVITE_INSTALL_PAGE_TITLE}</title>
        <MobileInstallApp
          universalLinkUrl={createOnboardingUniversalLinkUrl(token)}
          deepLinkUrl={createOnboardingDeepLinkUrl(token)}
          title={INVITE_INSTALL_TITLE}
          tenantDisplayName={validationResult?.tenant_display_name}
          ctaLabel={INVITE_INSTALL_CTA_LABEL}
        >
          {({ storeName, isIos }) => (
            <>
              <InstallStep number={1}>
                Tap{' '}
                <Text
                  as='strong'
                  size={TextSizes.MD}
                  weight={FontWeights.SEMIBOLD}
                >
                  {INVITE_INSTALL_CTA_LABEL}
                </Text>{' '}
                — the app opens if it is installed
              </InstallStep>
              <InstallStep number={2}>
                No app? {storeName} opens. Install NewCore,{' '}
                {isIos ? (
                  <>
                    then tap{' '}
                    <Text
                      as='strong'
                      size={TextSizes.MD}
                      weight={FontWeights.SEMIBOLD}
                    >
                      ◀ Safari
                    </Text>{' '}
                    to return here
                  </>
                ) : (
                  'then return here'
                )}
              </InstallStep>
              <InstallStep number={3}>
                Tap the button again to activate in the app
              </InstallStep>
            </>
          )}
        </MobileInstallApp>
      </>
    );
  }

  if (!token || !validationResult) {
    return null;
  }

  return (
    <OnboardingWizardContainer>
      <OnboardingBackground />
      <Onboarding.Dialog open={true} dismissible={false} size='md'>
        <UserOnboardingWizard
          token={token}
          userName={
            validationResult.user_first_name ??
            validationResult.user_display_name
          }
          isMobileOnboardingEligible={isMobileOnboardingEligible}
          tenantSlug={validationResult.tenant_slug}
          onComplete={handleComplete}
        />
      </Onboarding.Dialog>
    </OnboardingWizardContainer>
  );
}

export default OnboardingPage;
